import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import {
  CONTEST_DATE,
  EURO_QUIZ_INFO_PAGE_URL,
  REGISTRATION_END_DATE,
  REGISTRATION_START_DATE,
  TUTORIAL_REGISTRATION_END_DATE,
  TUTORIAL_REGISTRATION_START_DATE,
  getContestAnnouncementMessage,
  getContestEndedMessage,
  getContestInProgressMessage,
  getRegistrationClosedMessage,
  getRegistrationOpenMessage,
  getTutorialAnnouncementMessage,
  getTutorialRegistrationOpenMessage,
} from '../common/constants';
import { CountdownContentItem, CountdownData } from '../types';
import { getLocalStorage } from './cookies';
import { getAdjustedNow, getContestEnd, getContestStart } from './contest-timing';

type Schedule = {
  tutorialRegistrationStart: Date;
  tutorialRegistrationEnd: Date;
  registrationStart: Date;
  registrationEnd: Date;
  contestDate: Date;
};

const isDemoMode = () => process.env.REACT_APP_DEMO === 'true';

const resolveEffectiveDate = (storageKey: string, fallback: Date) => {
  const storedValue = getLocalStorage(storageKey);

  if (!isDemoMode() || !storedValue) {
    return fallback;
  }

  const parsedDate = new Date(storedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return fallback;
  }

  return parsedDate;
};

const getSchedule = (): Schedule => ({
  tutorialRegistrationStart: TUTORIAL_REGISTRATION_START_DATE,
  tutorialRegistrationEnd: TUTORIAL_REGISTRATION_END_DATE,
  registrationStart: resolveEffectiveDate('registration-start-date', REGISTRATION_START_DATE),
  registrationEnd: resolveEffectiveDate('registration-end-date', REGISTRATION_END_DATE),
  contestDate: resolveEffectiveDate('contest-date', CONTEST_DATE),
});

const isWithinInclusiveWindow = (now: number, start: Date, end: Date) =>
  now >= start.getTime() && now <= end.getTime();

const isAfterDate = (now: number, date: Date) => now > date.getTime();

const formatRomanianDate = (date: Date, pattern = 'd MMMM yyyy') =>
  format(date, pattern, { locale: ro });

const formatRomanianDateTime = (date: Date) =>
  `${formatRomanianDate(date)} ora ${format(date, 'HH:mm')}`;

const getEmptyCountdownData = (): CountdownData => ({});

const createContentItem = (text: string, link?: string): CountdownContentItem => ({
  text,
  ...(link ? { link } : {}),
});

const createCountdownData = (content: CountdownContentItem[], endTime?: number): CountdownData => ({
  endTime,
  content,
});

export const isTutorialRegistrationStarted = () => {
  const now = getAdjustedNow();
  const schedule = getSchedule();

  return isWithinInclusiveWindow(
    now,
    schedule.tutorialRegistrationStart,
    schedule.tutorialRegistrationEnd,
  );
};

export const isTutorialRegistrationEnded = () => {
  const now = getAdjustedNow();
  const schedule = getSchedule();

  return isAfterDate(now, schedule.tutorialRegistrationEnd);
};

export const isTutorialInProgress = () => {
  const now = getAdjustedNow();
  const schedule = getSchedule();
  const tutorialRegistrationEnd = schedule.tutorialRegistrationEnd.getTime();
  const registrationStart = schedule.registrationStart.getTime();

  return now > tutorialRegistrationEnd && now < registrationStart;
};

export const isTutorialEnded = () => {
  const now = getAdjustedNow();
  const schedule = getSchedule();

  return isAfterDate(now, schedule.tutorialRegistrationEnd);
};

export const isRegistrationStarted = () => {
  const now = getAdjustedNow();
  const schedule = getSchedule();

  return isWithinInclusiveWindow(now, schedule.registrationStart, schedule.registrationEnd);
};

export const isRegistrationEnded = () => {
  const now = getAdjustedNow();
  const schedule = getSchedule();

  return isAfterDate(now, schedule.registrationEnd);
};

export const isContestStarted = () => {
  const now = getAdjustedNow();
  return now >= getContestStart() && now < getContestEnd();
};

export const isContestEnded = () => {
  return getAdjustedNow() > getContestEnd();
};

const buildTutorialCountdownData = (schedule: Schedule): CountdownData => {
  if (!isTutorialRegistrationStarted() && !isTutorialRegistrationEnded()) {
    const tutorialDateLabel = formatRomanianDate(schedule.tutorialRegistrationEnd, 'd MMMM');
    const tutorialStartLabel = formatRomanianDate(schedule.tutorialRegistrationStart, 'd MMMM');
    const tutorialEndLabel = formatRomanianDate(schedule.tutorialRegistrationEnd, 'd MMMM');

    return createCountdownData(
      [
        createContentItem(
          getTutorialAnnouncementMessage(tutorialDateLabel, tutorialStartLabel, tutorialEndLabel),
        ),
      ],
      schedule.tutorialRegistrationStart.getTime(),
    );
  }

  if (isTutorialRegistrationStarted()) {
    const tutorialDateLabel = formatRomanianDate(schedule.tutorialRegistrationEnd, 'd MMMM');

    return createCountdownData(
      [createContentItem(getTutorialRegistrationOpenMessage(tutorialDateLabel))],
      schedule.tutorialRegistrationEnd.getTime(),
    );
  }

  return getEmptyCountdownData();
};

const getRegistrationCountdownData = (schedule: Schedule): CountdownData => {
  if (!isRegistrationStarted() && !isRegistrationEnded()) {
    const registrationStartLabel = formatRomanianDate(schedule.registrationStart);
    const registrationEndLabel = formatRomanianDate(schedule.registrationEnd);

    const contents = getRegistrationClosedMessage(registrationStartLabel, registrationEndLabel).map(
      (item, index) => createContentItem(item, index === 0 ? EURO_QUIZ_INFO_PAGE_URL : undefined),
    );

    return createCountdownData([...contents], schedule.registrationStart.getTime());
  }

  if (isRegistrationStarted()) {
    const registrationEndLabel = formatRomanianDate(schedule.registrationEnd);

    const contents = getRegistrationOpenMessage(registrationEndLabel).map((item) =>
      createContentItem(item),
    );

    return createCountdownData([...contents], schedule.registrationEnd.getTime());
  }

  return getEmptyCountdownData();
};

const buildContestCountdownData = (schedule: Schedule): CountdownData => {
  if (isRegistrationEnded() && !isContestStarted() && !isContestEnded()) {
    const contestDateTimeLabel = formatRomanianDateTime(schedule.contestDate);

    return createCountdownData(
      [createContentItem(getContestAnnouncementMessage(contestDateTimeLabel))],
      getContestStart(),
    );
  }

  if (isContestStarted()) {
    const contestTimeLabel = format(schedule.contestDate, 'HH:mm');

    return createCountdownData(
      [createContentItem(getContestInProgressMessage(contestTimeLabel))],
      getContestEnd(),
    );
  }

  return getEmptyCountdownData();
};

export const getCountdownData = (): CountdownData => {
  const schedule = getSchedule();
  const tutorialCountdownData = buildTutorialCountdownData(schedule);

  if (tutorialCountdownData.content?.length) {
    return tutorialCountdownData;
  }

  const registrationCountdownData = getRegistrationCountdownData(schedule);

  if (registrationCountdownData.content?.length) {
    return registrationCountdownData;
  }

  const contestCountdownData = buildContestCountdownData(schedule);

  if (contestCountdownData.content?.length || contestCountdownData.endTime) {
    return contestCountdownData;
  }

  return createCountdownData(
    getContestEndedMessage().map((item, index) =>
      createContentItem(item, index === 2 ? EURO_QUIZ_INFO_PAGE_URL : undefined),
    ),
  );
};
